import { sortOrder } from "util/customers/deliveryTimings";
import { getTourDeliveryTimings } from "util/helpers";

export function formatDeparture(departure, t) {
  if (!departure) return "";
  const depStr = String(departure).trim();
  if (t) {
    const direct = t(depStr);
    if (direct && direct !== depStr) {
      return direct;
    }
    const upper = t(depStr.toUpperCase());
    if (upper && upper !== depStr.toUpperCase()) {
      return upper;
    }
  }
  return depStr
    .toLowerCase()
    .split(" ")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function sortTourDepartures(departures) {
  if (!departures) return [];
  const list = Array.isArray(departures)
    ? [...departures]
    : typeof departures[Symbol.iterator] === "function"
      ? [...departures]
      : [departures];
  return list.sort((a, b) => {
    const aStr = String(a ?? "").toUpperCase();
    const bStr = String(b ?? "").toUpperCase();
    const aIdx = sortOrder.indexOf(aStr);
    const bIdx = sortOrder.indexOf(bStr);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return aStr.localeCompare(bStr);
  });
}

export function getCustomerTourEntries(rowData, t) {
  if (!rowData) return [];
  const timings = rowData.CustomerDeliveryTimings || [];
  if (timings.length > 0) {
    const tourMap = new Map();
    for (const cdt of timings) {
      const tdt = cdt.ToursDeliveryTiming;
      if (tdt) {
        const tourName = tdt.Tour?.name || "No Tour";
        if (!tourMap.has(tourName)) {
          tourMap.set(tourName, new Set());
        }
        if (tdt.delivery_timing) {
          tourMap.get(tourName).add(tdt.delivery_timing);
        }
      }
    }
    const entries = [];
    for (const [name, depsSet] of tourMap.entries()) {
      const departures = sortTourDepartures(depsSet);
      const formattedDepartures = departures.map((dep) => formatDeparture(dep, t));
      const depText =
        formattedDepartures.length > 0 ? ` (${formattedDepartures.join(", ")})` : "";
      const rawDepText =
        departures.length > 0 ? ` (${departures.join(", ")})` : "";
      entries.push({
        name,
        departures,
        formattedDepartures,
        displayString: `${name}${depText}`,
        rawDisplayString: `${name}${rawDepText}`,
      });
    }
    if (entries.length > 0) {
      return entries;
    }
  }

  if (rowData.Tour?.name) {
    const rawDeps = rowData.departure
      ? [rowData.departure]
      : getTourDeliveryTimings(rowData.Tour);
    const departures = sortTourDepartures(rawDeps);
    const formattedDepartures = departures.map((dep) => formatDeparture(dep, t));
    const depText =
      formattedDepartures.length > 0 ? ` (${formattedDepartures.join(", ")})` : "";
    const rawDepText =
      departures.length > 0 ? ` (${departures.join(", ")})` : "";
    return [
      {
        name: rowData.Tour.name,
        departures,
        formattedDepartures,
        displayString: `${rowData.Tour.name}${depText}`,
        rawDisplayString: `${rowData.Tour.name}${rawDepText}`,
      },
    ];
  }

  return [];
}

export function getCustomerTourSortString(rowData, t) {
  const entries = getCustomerTourEntries(rowData, t);
  if (!entries.length) return rowData?.Tour?.name || "";
  return entries.map((e) => e.displayString).join(", ");
}

export function matchCustomerTourFilter(query, rowData, t) {
  if (query === undefined || query === null) return true;
  const q = String(query).trim();
  if (!q) return true;

  const entries = getCustomerTourEntries(rowData, t);
  if (!entries.length) return false;

  const lowerQuery = q.toLowerCase();

  const normalize = (str) =>
    String(str || "")
      .toLowerCase()
      .replace(/[(),]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const normQuery = normalize(lowerQuery);
  const queryTokens = normQuery.split(" ").filter(Boolean);

  for (const entry of entries) {
    if (entry.displayString.toLowerCase().includes(lowerQuery)) {
      return true;
    }
    if (entry.rawDisplayString.toLowerCase().includes(lowerQuery)) {
      return true;
    }
    if (normQuery && normalize(entry.displayString).includes(normQuery)) {
      return true;
    }
    if (normQuery && normalize(entry.rawDisplayString).includes(normQuery)) {
      return true;
    }
  }

  for (const entry of entries) {
    const formattedList = entry.formattedDepartures.join(", ").toLowerCase();
    const rawList = entry.departures.join(", ").toLowerCase();
    if (formattedList.includes(lowerQuery) || rawList.includes(lowerQuery)) {
      return true;
    }
    if (
      entry.formattedDepartures.some((d) => d.toLowerCase().includes(lowerQuery)) ||
      entry.departures.some((d) => String(d).toLowerCase().includes(lowerQuery))
    ) {
      return true;
    }
  }

  if (queryTokens.length > 0) {
    for (const entry of entries) {
      const entryHaystack = normalize(
        `${entry.name} ${entry.formattedDepartures.join(" ")} ${entry.departures.join(" ")}`
      );
      if (queryTokens.every((token) => entryHaystack.includes(token))) {
        return true;
      }
    }
  }

  return false;
}
